// =============================================================================
// KvoxWebHost: the one thing a web slice reads from the app that mounts it
// =============================================================================
//
// A slice never imports the app's HTTP client, its auth context or its router
// helpers. It reads this port through `useKvoxHost()`, and the app (the web
// edition, KVox Desktop) provides it once with `KvoxWebHostProvider`. That is
// what lets the same pages and hooks run in a host that is not the KVox web
// edition. A packaged page that renders without a host refuses loudly
// (`KvoxHostMissingError`) instead of failing later on an undefined transport.
// =============================================================================
import { createContext, createElement, useContext } from 'react';
/**
 * Whether a host's features say it records and transcribes audio.
 *
 * @param features - the host's deployment switches.
 * @returns `false` only when `features.transcription` is exactly `false`.
 *
 * @stability experimental
 */
export function isKvoxTranscriptionEnabled(features) {
    return features.transcription !== false;
}
/**
 * Thrown when a packaged page or hook runs without a `KvoxWebHostProvider`.
 *
 * @stability experimental
 */
export class KvoxHostMissingError extends Error {
    /** Names the missing provider so a stack trace is self-explanatory. */
    constructor() {
        super('No KvoxWebHost: a @marinoscar/kvox-web page or hook needs a <KvoxWebHostProvider host={...}> above it. ' +
            'The app binds the host once (the web edition does it in apps/web/src/platform-kvox/KvoxHostBinding.tsx).');
        this.name = 'KvoxHostMissingError';
    }
}
const KvoxHostContext = createContext(null);
/**
 * Provides the host to every slice below it.
 *
 * @param props - The host and the subtree.
 * @returns The subtree inside the host context.
 *
 * @stability experimental
 */
export function KvoxWebHostProvider(props) {
    return createElement(KvoxHostContext.Provider, { value: props.host }, props.children);
}
/**
 * The host of the nearest provider.
 *
 * @returns The host.
 * @throws {@link KvoxHostMissingError} when there is no provider above.
 *
 * @stability experimental
 */
export function useKvoxHost() {
    const host = useContext(KvoxHostContext);
    if (host === null)
        throw new KvoxHostMissingError();
    return host;
}
/**
 * The id of the most recent notification whose event key starts with `prefix`,
 * from the host's {@link KvoxWebHost.useLatestEventId}; `null` when the host
 * has none or there is no such notification.
 *
 * @param prefix - The event-key prefix, for example `transcripts.`.
 * @returns The notification id, which changes exactly when a NEW matching event arrives.
 *
 * @stability experimental
 */
export function useKvoxLatestEventId(prefix) {
    const host = useKvoxHost();
    const hook = host.useLatestEventId;
    // A host's hook is fixed for its lifetime, so the hook order is stable.
    return hook ? hook(prefix) : null;
}
/**
 * Whether the host records and transcribes audio, read on every render.
 *
 * @remarks
 * `true` unless the host's `features().transcription` is exactly `false`. Unlike
 * the other host hooks it does not throw without a `KvoxWebHostProvider`: a
 * presentational component that only words its copy with it answers `true`
 * (the KVox web wording) when it is drawn on its own, as in a story or a test.
 *
 * @returns `false` for a host without transcription (KVox Desktop).
 *
 * @stability experimental
 */
export function useKvoxTranscriptionEnabled() {
    const host = useContext(KvoxHostContext);
    return host === null ? true : isKvoxTranscriptionEnabled(host.features());
}
/**
 * The signed-in user, read from the host on every render.
 *
 * @returns The viewer, or `null` when nobody is signed in.
 *
 * @stability experimental
 */
export function useKvoxViewer() {
    return useKvoxHost().viewer();
}
/**
 * Whether the signed-in user holds a permission. `false` when nobody is signed in.
 *
 * @param permission - The exact permission string the API enforces.
 * @returns `true` when the viewer holds it.
 *
 * @stability experimental
 */
export function useHasPermission(permission) {
    return useKvoxViewer()?.permissions.has(permission) ?? false;
}
/**
 * A predicate form of {@link useHasPermission}, for a component that checks
 * several permissions (including inside a callback).
 *
 * @returns A function answering whether the viewer holds a permission.
 *
 * @stability experimental
 */
export function useKvoxPermissionCheck() {
    const viewer = useKvoxViewer();
    return (permission) => viewer?.permissions.has(permission) ?? false;
}
